import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { FilterBar, FilterBarActions, FilterBarRow } from 'shared/mui/Filter/FilterBar';
import { FilterApplyButton, FilterResetButton } from 'shared/mui/Filter/FilterButtons';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { AutocompleteMultiselectInput } from 'shared/react-admin/Inputs';
import { ReferenceArrayInput } from 'shared/react-admin/ReferenceInputs';
import { Currency } from 'types/embedded/currency';
import { cleanEmpty } from 'utils/cleanEmpty';

type Props = {
  merchantId?: string;
};

type MerchantsShowLimitsFiltersValues = {
  gateway: string[];
  currency: string[];
};

const DEFAULT_VALUES: MerchantsShowLimitsFiltersValues = { gateway: [], currency: [] };

export const MerchantsShowLimitsFilters: React.FC<Props> = (props) => {
  const translate = useTypedTranslate();
  const { filterValues } = useListContext();
  const { form, onReset, onSubmit, appliedFilters } = useFilters<MerchantsShowLimitsFiltersValues>({
    defaultValues: { ...DEFAULT_VALUES, ...filterValues },
    resetValues: DEFAULT_VALUES,
  });

  useSyncFilter<MerchantsShowLimitsFiltersValues>({ appliedFilters });

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FilterBar>
          <FilterBarRow sx={{ gridTemplateColumns: '1fr 1fr 272px' }}>
            <ReferenceArrayInput
              filter={cleanEmpty({
                merchantAccounts: {
                  merchants: [props.merchantId],
                },
              })}
              perPage={100}
              reference="gateways"
              source="gateway"
            >
              <AutocompleteMultiselectInput
                filterToQuery={(searchText: string) => ({ name: searchText })}
                helperText={false}
                label={translate('app.filter.gateways')}
                name="gateway"
                optionText="name"
              />
            </ReferenceArrayInput>
            <ReferenceArrayInput
              filter={{ active: true }}
              perPage={50}
              reference="currencies"
              source="currency"
            >
              <AutocompleteMultiselectInput
                filterToQuery={(searchText: string) => ({ currency: searchText })}
                getOptionLabel={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
                helperText={false}
                label={translate('app.filter.currencies')}
                name="currency"
              />
            </ReferenceArrayInput>
            <FilterBarActions>
              <FilterResetButton label={translate('app.buttons.reset')} onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterBarActions>
          </FilterBarRow>
        </FilterBar>
      </form>
    </FormProvider>
  );
};
